// src/services/informationPublicationsService.ts

import { actaApi } from './actaApi';

/**
 * Criação de uma nova publicação de informação (um só arquivo).
 */
export interface InformationPublicationCreatePayload {
  entity_id: string;
  file: File;
}

export async function createInformationPublication(
  payload: InformationPublicationCreatePayload
): Promise<any> {
  const formData = new FormData();
  formData.append('entity_id', payload.entity_id);
  // Por solicitação, permitimos somente 1 arquivo
  formData.append('files', payload.file);

  const response = await actaApi.post('/information-publications', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

/**
 * Lista (GET /information-publications?entity_id=xxxx)
 */
export async function getAllInformationPublications(
  entityId: string
): Promise<any[]> {
  const response = await actaApi.get<any[]>(`/information-publications?entity_id=${entityId}`);
  return response.data;
}

/**
 * Detalha (GET /information-publications/{publicationId}?entity_id=xxxx)
 */
export async function getInformationPublication(
  informationId: string,
  entityId: string
): Promise<any> {
  const response = await actaApi.get<any>(
    `/information-publications/${informationId}?entity_id=${entityId}`
  );
  return response.data;
}

/**
 * Atualiza a categoria principal e as secundárias
 * (PUT /information-publications/{id}/categories)
 */
export async function updateInformationPublicationCategories(
  informationId: string,
  mainCategoryId: string,
  categoryIds: string[]
): Promise<any> {
  const payload = {
    main_category_id: mainCategoryId,
    category_ids: categoryIds,
  };
  const response = await actaApi.put(
    `/information-publications/${informationId}/categories`,
    payload
  );
  return response.data;
}

/**
 * Busca todas as categorias de informação (GET /information-categories)
 */
export async function getAllInformationCategories(): Promise<any[]> {
  const response = await actaApi.get<any[]>('/information-categories');
  return response.data;
}

// ============================================================================
// CRUD para InformationCategory
// ============================================================================

export interface ICategory {
  id: string;
  category: string;
  description: string;
  parent_id?: string | null;
  children?: ICategory[];
  icon?: string | null;
}

export async function listCategories(entityId: string): Promise<ICategory[]> {
  const response = await actaApi.get(`/information-categories?entity_id=${entityId}`);
  return response.data;
}

export async function getCategory(categoryId: string, entityId: string): Promise<ICategory> {
  const response = await actaApi.get(`/information-categories/${categoryId}?entity_id=${entityId}`);
  return response.data;
}

// O tipo Omit<ICategory, 'id' | 'children'> garante que estamos enviando os dados corretos para a criação.
export async function createCategory(data: Omit<ICategory, 'id' | 'children'>): Promise<ICategory> {
  // O entity_id vai no corpo da requisição, não na URL.
  const response = await actaApi.post(`/information-categories`, data);
  return response.data;
}

export async function updateCategory(categoryId: string, data: Partial<Omit<ICategory, 'id'>>, entityId: string): Promise<ICategory> {
  const response = await actaApi.put(`/information-categories/${categoryId}?entity_id=${entityId}`, data);
  return response.data;
}

export async function deleteCategory(categoryId: string, entityId: string): Promise<void> {
  await actaApi.delete(`/information-categories/${categoryId}?entity_id=${entityId}`);
}

/**
 * Atualiza detalhes (autor, data) (PUT /information-publications/{id})
 */
export async function updateInformationPublicationDetails(
  informationId: string,
  author: string | null,
  document_date: string | null
): Promise<any> {
  const formData = new FormData();
  if (author) {
    formData.append('author', author);
  }
  if (document_date) {
    formData.append('document_date', document_date);
  }
  const response = await actaApi.put<any>(
    `/information-publications/${informationId}`,
    formData
  );
  return response.data;
}

/**
 * Exclui (DELETE /information-publications/{id})
 */
export async function deleteInformationPublication(
  informationId: string
): Promise<any> {
  const response = await actaApi.delete<any>(`/information-publications/${informationId}`);
  return response.data;
}

/**
 * Retorna a URL pública absoluta para o PDF de uma publicação de informação,
 * aproveitando a instância do Axios para respeitar o baseURL definido.
 * 
 * Ex.: /public/information-publications/{informationId}/pdf
 */
export function buildPublicInformationPdfUrl(informationId: string): string {
  // `getUri(...)` retorna a URL absoluta, juntando baseURL + rota
  // Note que, se seu backend for algo como "http://localhost:8002/api",
  // então a rota será: http://localhost:8002/api/public/information-publications/{id}/pdf
  return actaApi.getUri({
    url: `/public/information-publications/${informationId}/pdf`,
  });
}
